import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';
import ThemeToggle from './ThemeToggle';
import maintenanceUtils, { generateSystemCoords, generateAccessCode, getEstimatedMaintenanceTime } from '../../utils/maintenanceUtils';
import './MaintenanceMode.css';
import {Spotify, Twitch, SoundCloudLight, InstagramLight, Discord} from "@ridemountainpig/svgl-react";


const MaintenanceMode = () => {
    const [systemStatus, setSystemStatus] = useState('OFFLINE');
    const [progress, setProgress] = useState(0);
    const [scanLines, setScanLines] = useState([]);
    const [coordinates, setCoordinates] = useState('');
    const [accessCode, setAccessCode] = useState('');
    const [eta, setEta] = useState('');
    const message = maintenanceUtils.formatMaintenanceMessage();

    useEffect(() => {
        // Initialize dynamic values
        setCoordinates(generateSystemCoords());
        setAccessCode(generateAccessCode());
        setEta(getEstimatedMaintenanceTime());

        // Simulate system diagnostic progress
        const progressInterval = setInterval(() => {
            setProgress(prev => {
                if (prev >= 100) return 0;
                return prev + Math.random() * 5;
            });
        }, 500);

        // Generate random scan lines
        const generateScanLines = () => {
            const lines = Array.from({ length: 8 }, (_, i) => ({
                id: i,
                delay: Math.random() * 2,
                duration: 2 + Math.random() * 3
            }));
            setScanLines(lines);
        };

        generateScanLines();
        const scanInterval = setInterval(generateScanLines, 5000);

        return () => {
            clearInterval(progressInterval);
            clearInterval(scanInterval);
        };
    }, []);

    const socialLinks = [
        {
            name: 'Spotify', 
            handle: '@jxles',
            url: 'https://open.spotify.com/artist/1cG9a9nCkAJgfDkH5YfC1W', 
            icon: <Spotify width="1.5em" height="1.5em" />,
            platform: 'spotify',
            isActive: true
        },
        { 
            name: 'SoundCloud', 
            handle: 'jxles',
            url: 'https://soundcloud.com/itsjxles', 
            icon: <SoundCloudLight width="1.5em" height="1.5em" />,
            platform: 'soundcloud',
            isActive: true
        },
        { 
            name: 'Instagram', 
            handle: '@jxles',
            url: 'https://instagram.com/its_jxles', 
            icon: <InstagramLight width="1.5em" height="1.5em" />,
            platform: 'instagram',
            isActive: true
        },
        {
            name: 'Discord', 
            handle: '.jxles',
            url: 'https://discord.gg/C3ZZGzNtxy', 
            icon: <Discord width="1.5em" height="1.5em" />,
            platform: 'discord',
            isActive: true
        },
        { 
            name: 'Twitch', 
            handle: '@cypher_live',
            url: 'https://www.twitch.tv/itsjxles', 
            icon: <Twitch width="1.5em" height="1.5em" />,
            platform: 'twitch',
            isActive: false
        },
    ];

    return (
        <div className="maintenance-mode">
            {/* Animated background grid - Reduced for performance */}
            <div className="maintenance-grid">
                {Array.from({ length: 48 }, (_, i) => (
                    <div key={i} className="grid-cell" />
                ))}
            </div>

            {/* Scan lines */}
            {scanLines.map(line => (
                <motion.div
                    key={line.id}
                    className="scan-line"
                    initial={{ x: '-100%', opacity: 0 }}
                    animate={{ x: '100vw', opacity: [0, 1, 0] }}
                    transition={{
                        duration: line.duration,
                        delay: line.delay,
                        repeat: Infinity,
                        ease: 'linear'
                    }}
                    style={{ top: `${10 + line.id * 12}%` }}
                />
            ))}

            <div className="maintenance-container">
                {/* Main maintenance panel */}
                <motion.div
                    className="maintenance-panel"
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                >
                    {/* Header */}
                    <div className="panel-header">
                        <div className="status-indicator maintenance-status-indicator">
                            <div className="status-dot offline"></div>
                            <span className="status-text">SYSTEM STATUS: {systemStatus}</span>
                        </div>
                        <div className="timestamp">
                            {new Date().toLocaleString().replace(/[/:]/g, '.')}
                        </div>
                    </div>

                    {/* Main content */}
                    <div className="panel-content">
                        <motion.div
                            className="maintenance-logo"
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 1, delay: 0.5 }}
                        >
                            <div className="logo-text">jxles</div>
                            <div className="logo-subtitle">PILOT INTERFACE</div>
                        </motion.div>

                        <motion.div
                            className="maintenance-message"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.8, delay: 1 }}
                        >
                            <h2 className="message-title">{message.title}</h2>
                            <p className="message-text">
                                {message.subtitle}
                                <br />
                                {message.details}
                            </p>
                            
                            
                            {/* Progress bar */}
                            {/* <div className="diagnostic-section">
                                <div className="diagnostic-label">DIAGNOSTIC PROGRESS:</div>
                                <div className="progress-bar">
                                    <div 
                                        className="progress-fill"
                                        style={{ width: `${Math.min(progress, 100)}%` }}
                                    />
                                </div>
                                <div className="progress-text">{Math.floor(progress)}% COMPLETE</div>
                            </div> */}

                            {/* System messages */}
                            <div className="system-messages">
                                <div className="message-line">► AUDIO ENGINE: OPTIMIZING...</div>
                                <div className="message-line">► NEURAL NETWORKS: RECALIBRATING...</div>
                                <div className="message-line">► COMBAT SYSTEMS: UPGRADING...</div>
                                <div className="message-line">► ETA: {eta}</div>
                            </div>
                        </motion.div>

                        {/* Social links */}
                        {socialLinks.length > 0 && (
                            <motion.div
                                className="social-section"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 1.5 }}
                            >
                                <div className="social-header">COMMUNICATION CHANNELS ACTIVE:</div>
                                <div className="social-grid">
                                    {socialLinks.map((link, index) => (
                                        <motion.a
                                            key={link.name}
                                            href={link.url}
                                            disabled={!link.isActive}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="maintenance-social-link"
                                            initial={{ opacity: 0, scale: 0.8 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            transition={{ duration: 0.3, delay: 1.7 + index * 0.1 }}
                                            whileHover={{ scale: 1.1, glow: true }}
                                            whileTap={{ scale: 0.95 }}
                                        >
                                            <span className="maintenance-social-icon">{link.icon}</span>
                                            <span className="maintenance-social-name">{link.name}</span>
                                        </motion.a>
                                    ))}
                                </div>
                            </motion.div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="panel-footer">
                        <div className="footer-left">
                            <div className="coordinates">COORDS: {coordinates}</div>
                            <div className="access-code">ACCESS CODE: {accessCode}</div>
                        </div>
                        <div className="footer-right">
                            <ThemeToggle showLabel={false} size="small" />
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default MaintenanceMode;
